import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getCategories, createCategory, deleteCategory } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Tag } from "lucide-react";

export default function Categories() {
  const { t } = useTranslation();
  const { canEdit, canDelete } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await getCategories();
      setCategories(response.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createCategory(formData);
      toast.success(t('categories.added'));
      setDialogOpen(false);
      setFormData({ name: "", description: "" });
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(t('categories.confirmDelete', { name }))) return;
    try {
      await deleteCategory(id);
      toast.success(t('categories.deleted'));
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  return (
    <div data-testid="categories-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{t('categories.title')}</h1>
          <p className="page-subtitle">{t('categories.subtitle')}</p>
        </div>
        {canEdit() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2" data-testid="add-category-btn">
                <Plus className="w-4 h-4" />
                {t('categories.add')}
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="category-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('categories.new')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="form-label">{t('categories.name')}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Docking Station"
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <Label className="form-label">{t('categories.description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kurze Beschreibung der Kategorie..."
                  rows={3}
                  data-testid="category-description-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="btn-primary" data-testid="category-submit-btn">
                  {t('common.add')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8 text-slate-500">
            {t('common.loading')}
          </div>
        ) : categories.length === 0 ? (
          <div className="col-span-full text-center py-8 text-slate-500">
            {t('categories.noEntries')}
          </div>
        ) : (
          categories.map((category) => (
            <div 
              key={category.id} 
              className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow"
              data-testid={`category-card-${category.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Tag className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{category.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {category.description || t('categories.noDescription')}
                    </p>
                  </div>
                </div>
                {canDelete() && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(category.id, category.name)}
                    data-testid={`delete-category-${category.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
